import { defineStore } from 'pinia';
import { WebshopCartService } from '@/services/api';
import { Order, OrderItem, Product } from '@/models';
import { useSessionStore } from '@/stores/session.store';
import { watch, type WatchStopHandle } from 'vue';

interface State {
  _cart: Order | null;
  isCheckingOut?: boolean;
  _organizationWatcher?: WatchStopHandle;
  _cartService?: WebshopCartService;
  _currentOrgId?: string;
}

const initialState: State = {
  _cart: null
};

export const useWebshopCartStore = defineStore('webshopCart', {
  state: (): State => ({
    ...initialState
  }),
  getters: {
    cart: (state: State): Order | null => state._cart,
    cartItems: (state: State): OrderItem[] => state._cart?.orderItems ?? [],
    cartItemsCount: (state: State): number =>
      state._cart?.orderItems?.reduce((total, item) => total + item.quantity, 0) || 0,
    hasPatientItems: (state: State): boolean =>
      state._cart?.orderItems?.some((item) => item.patientId) || false
  },
  actions: {
    initializeOrganizationWatcher(): void {
      if (this._organizationWatcher) {
        return; // Already initialized
      }

      const sessionStore = useSessionStore();

      this._organizationWatcher = watch(
        () => sessionStore.currentOrganisationId,
        (newOrgId, oldOrgId) => {
          if (newOrgId !== oldOrgId && oldOrgId) {
            this.clearCart();
            // Clear cached service when organization changes
            this._cartService = null;
            this._currentOrgId = null;
          }
        }
      );
    },

    clearCart(): void {
      this._cart = null;
    },

    cleanup(): void {
      if (this._organizationWatcher) {
        this._organizationWatcher();
        this._organizationWatcher = null;
      }
      this.clearCart();
      this._cartService = null;
      this._currentOrgId = null;
    },
    enrichOrderItemsWithPrivateLabelImage(orderItems?: OrderItem[]): OrderItem[] {
      if (!orderItems?.length) {
        return orderItems || [];
      }
      return orderItems.map((orderItem: OrderItem) => {
        const product: Product | undefined = orderItem.product;
        const image = product?.has_private_label ? product?.image : null;
        if (!image) {
          return orderItem;
        }
        const productOptions = { ...(orderItem.productOptions || {}), image };
        return { ...orderItem, productOptions } as OrderItem;
      });
    },
    getCartService() {
      const sessionStore = useSessionStore();
      const currentOrgId = sessionStore.currentOrganisationId;

      // Return cached service if organization hasn't changed
      if (this._cartService && this._currentOrgId === currentOrgId) {
        return this._cartService;
      }

      // Create and cache new service for current organization
      this._cartService = new WebshopCartService(currentOrgId);
      this._currentOrgId = currentOrgId;
      return this._cartService;
    },
    async fetchCart(): Promise<void> {
      this.initializeOrganizationWatcher();

      try {
        const cartService = this.getCartService();
        const cart = await cartService.getCart();
        if (cart) {
          cart.orderItems = this.enrichOrderItemsWithPrivateLabelImage(cart.orderItems);
        }
        this._cart = cart;
      } catch (error: any) {
        if (error?.response?.status === 204 || error?.response?.status === 404) {
          this._cart = null;
        } else {
          console.error(`Failed to fetch cart:`, error);
          this._cart = null;
          throw new Error(`Failed to fetch cart: ${error.message || error}`);
        }
      }
    },
    async addItem(orderItem: OrderItem) {
      try {
        const cartService = this.getCartService();
        const response = await cartService.addItem(orderItem);
        if (response) {
          // Preserve lensProperties from the item being added
          if (orderItem.productOptions?.lensProperties && response.orderItems) {
            response.orderItems = response.orderItems.map((item) => {
              // If this is the newly added item and it's missing lensProperties, add it
              if (item.mlsId === orderItem.mlsId &&
                  item.productName === orderItem.productName &&
                  (!item.productOptions?.lensProperties ||
                   item.productOptions.lensProperties.length === 0)) {
                return {
                  ...item,
                  productOptions: {
                    ...item.productOptions,
                    lensProperties: orderItem.productOptions.lensProperties
                  }
                };
              }
              return item;
            });
          }
          response.orderItems = this.enrichOrderItemsWithPrivateLabelImage(response.orderItems);
        }
        this._cart = response;
      } catch (error) {
        throw new Error(`Failed to add item to cart: ${error}`);
      }
    },
    async updateItem(itemId: string, orderItem: OrderItem) {
      try {
        const cartService = this.getCartService();
        const updatedItemFromApi = await cartService.updateItem(itemId, orderItem);

        if (!this._cart) {
          this._cart = {
            id: '',
            status: 'pending',
            orderNumber: '',
            orderDate: null,
            comments: null,
            fileBasePath: null,
            parameters: '{}',
            orderItems: []
          };
        }

        if (!this._cart.orderItems) {
          this._cart.orderItems = [];
        }

        const finalItemId = updatedItemFromApi?.id || itemId;
        const existingItemIndex = this._cart.orderItems.findIndex((item) => item.id === finalItemId);

        const orderItems = [...this._cart.orderItems];

        if (existingItemIndex >= 0) {
          const existingItem = orderItems[existingItemIndex];
          let itemToStore = updatedItemFromApi || orderItem;

          // ALWAYS preserve productOptions.lensProperties from existing item
          // This ensures MLS API order is maintained even after updates
          // The API response may include lensProperties but in wrong order, so we always use the existing one
          if (existingItem.productOptions?.lensProperties) {
            // Clone the item to avoid mutating the original
            itemToStore = {
              ...itemToStore,
              productOptions: {
                ...itemToStore.productOptions,
                lensProperties: existingItem.productOptions.lensProperties
              }
            };
          }

          orderItems[existingItemIndex] = itemToStore;
        } else if (updatedItemFromApi) {
          orderItems.push(updatedItemFromApi);
        }

        this._cart.orderItems = this.enrichOrderItemsWithPrivateLabelImage(orderItems);

        return this._cart.orderItems.find((item) => item.id === finalItemId) || null;
      } catch (error) {
        console.error('Failed to update item:', error);
        throw error;
      }
    },
    async removeItem(itemId: string) {
      try {
        const cartService = this.getCartService();
        await cartService.removeItem(itemId);
        if (this._cart?.orderItems) {
          this._cart.orderItems = this._cart.orderItems.filter((item) => item.id !== itemId);
        }
      } catch (error) {
        throw new Error(`Failed to remove item from cart: ${error}`);
      }
    },
    async checkout() {
      try {
        const cartService = this.getCartService();
        await cartService.checkout();
        this._cart = null;
      } catch (error) {
        throw new Error(`Failed to checkout: ${error}`);
      }
    }
  }
});
